import { useCallback, useEffect, useState } from "react";
import { MDBBtn, MDBInput, MDBCheckbox, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter } from "mdb-react-ui-kit";

import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import { mfmThumbUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import {
  getGameByCodigo, deleteGame, leaveGame, updateGameConfig, joinGamePublic, isOk,
} from "Projects/CHAMP_championsjourney_project/Utils/ChampService";
import CharacterSheet from "Projects/CHAMP_championsjourney_project/Components/CharacterSheet/CharacterSheet";

export default function GameDetail({ gameCodigo, onBack, onLoading }) {
  const [game, setGame] = useState(null);
  const [selectedCharId, setSelectedCharId] = useState(null);

  // config panel
  const [showConfig, setShowConfig] = useState(false);
  const [cfgNombre, setCfgNombre] = useState("");
  const [cfgMaxJugadores, setCfgMaxJugadores] = useState(6);
  const [cfgVistaPublica, setCfgVistaPublica] = useState(false);
  const [cfgModoEvolucion, setCfgModoEvolucion] = useState("VISIBLE");
  const [cfgSaving, setCfgSaving] = useState(false);

  // join-from-url modal
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinNombre, setJoinNombre] = useState("");
  const [joinSaving, setJoinSaving] = useState(false);

  const handleHideConfig = useCallback(() => setShowConfig(false), []);
  const handleHideJoin = useCallback(() => setShowJoinModal(false), []);

  const load = useCallback(async () => {
    onLoading && onLoading(true);
    try {
      const res = await getGameByCodigo(gameCodigo);
      if (isOk(res)) {
        setGame(res.DATA);
        setSelectedCharId((prev) => prev || res.DATA.idMiPersonaje || null);
      } else {
        toast.show({ message: res?.MESSAGE || "No se pudo cargar la partida", type: "danger" });
      }
    } finally {
      onLoading && onLoading(false);
    }
  }, [gameCodigo, onLoading]);

  useEffect(() => { load(); }, [load]);

  const openConfig = useCallback(() => {
    if (!game) return;
    setCfgNombre(game.nombre);
    setCfgMaxJugadores(game.maxJugadores ?? 6);
    setCfgVistaPublica(!!game.vistaPublica);
    setCfgModoEvolucion(game.modoEvolucion || "VISIBLE");
    setShowConfig(true);
  }, [game]);

  async function handleSaveConfig() {
    setCfgSaving(true);
    try {
      const res = await updateGameConfig(game.idPartida, {
        nombre: cfgNombre,
        maxJugadores: cfgMaxJugadores,
        vistaPublica: cfgVistaPublica,
        modoEvolucion: cfgModoEvolucion,
      });
      if (isOk(res)) {
        setGame(res.DATA);
        setShowConfig(false);
        toast.show({ message: "Configuración guardada", type: "success" });
      } else {
        toast.show({ message: res?.MESSAGE || "Error al guardar", type: "danger" });
      }
    } finally {
      setCfgSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm("¿Eliminar esta partida? Esta acción no se puede deshacer.")) return;
    const res = await deleteGame(game.idPartida);
    if (isOk(res)) {
      toast.show({ message: "Partida eliminada", type: "success" });
      onBack();
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo eliminar", type: "danger" });
    }
  }

  async function handleLeave() {
    if (!window.confirm("¿Salir de esta partida? Perderás tu personaje en ella.")) return;
    const res = await leaveGame(game.idPartida);
    if (isOk(res)) {
      toast.show({ message: "Has salido de la partida", type: "success" });
      onBack();
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo salir", type: "danger" });
    }
  }

  async function handleJoin() {
    if (!joinNombre.trim()) { toast.show({ message: "Indica un nombre para tu personaje", type: "warning" }); return; }
    setJoinSaving(true);
    try {
      const res = await joinGamePublic(gameCodigo, joinNombre.trim());
      if (isOk(res)) {
        toast.show({ message: "¡Te has unido a la partida!", type: "success" });
        setShowJoinModal(false);
        load();
      } else {
        toast.show({ message: res?.MESSAGE || "No se pudo unir", type: "danger" });
      }
    } finally {
      setJoinSaving(false);
    }
  }

  if (!game) return null;

  const isViewer = !game.rolUsuario;

  return (
    <div>
      <button className="champ-back-link" onClick={onBack}>← Volver a mis partidas</button>

      <div className="champ-game-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.75rem" }}>
          <div>
            <h3>{game.nombre}</h3>
            <div className="champ-text-muted">
              Región {game.regionInicio} · {game.estado} · creada por {game.creadoPor}
            </div>
            {(game.maxJugadores > 0 || game.vistaPublica) && (
              <div className="champ-text-muted" style={{ marginTop: "0.2rem", fontSize: "0.78rem" }}>
                {game.maxJugadores > 0 && (
                  <span style={{ marginRight: "0.75rem" }}>
                    👥 {(game.miembros || []).length}/{game.maxJugadores} jugadores
                    {game.lleno && <span className="champ-chip" style={{ marginLeft: "0.3rem", background: "#dc3545", color: "#fff" }}>Llena</span>}
                  </span>
                )}
                {game.vistaPublica && <span>🌐 Vista pública</span>}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="champ-text-muted" style={{ marginBottom: "0.3rem" }}>Código</div>
            <span className="champ-code">{game.codigo}</span>
          </div>
        </div>

        <div className="champ-members">
          {(game.miembros || []).map((m) => (
            <div
              key={m.idMiembro}
              className={"champ-member" + (!isViewer && selectedCharId === m.idPersonaje ? " active" : "") + (isViewer ? " champ-member-readonly" : "")}
              onClick={() => !isViewer && setSelectedCharId(m.idPersonaje)}
            >
              {m.avatarUuid
                ? <img src={mfmThumbUrl(m.avatarUuid)} alt={m.nombrePersonaje} className="champ-avatar" />
                : <div className="champ-avatar" />}
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>
                  {m.nombrePersonaje || m.apodo}
                  {m.rol === "GM" && <span className="champ-chip champ-chip-gm" style={{ marginLeft: "0.35rem" }}>GM</span>}
                </div>
                <div className="champ-text-muted" style={{ fontSize: "0.72rem" }}>
                  Nv. {m.nivel} · {m.numMedallas} 🏅
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="champ-mt-3" style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {isViewer ? (
            <>
              {game.lleno
                ? <span className="champ-text-muted">La partida está llena.</span>
                : <MDBBtn size="sm" color="success" onClick={() => setShowJoinModal(true)}>Unirse a la partida</MDBBtn>}
            </>
          ) : (
            <>
              {game.esCreador ? (
                <>
                  <MDBBtn size="sm" color="secondary" outline onClick={openConfig}>⚙ Configurar</MDBBtn>
                  <MDBBtn size="sm" color="danger" outline onClick={handleDelete}>Eliminar partida</MDBBtn>
                </>
              ) : (
                <MDBBtn size="sm" color="warning" outline onClick={handleLeave}>Salir de la partida</MDBBtn>
              )}
            </>
          )}
        </div>
      </div>

      {!isViewer && selectedCharId && (
        <CharacterSheet characterId={selectedCharId} onLoading={onLoading} modoEvolucion={game.modoEvolucion || "VISIBLE"} />
      )}

      {/* Config modal */}
      <MDBModal show={showConfig} onHide={handleHideConfig} tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Configurar partida</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={handleHideConfig} />
            </MDBModalHeader>
            <MDBModalBody>
              <div className="mb-3">
                <MDBInput label="Nombre de la partida" value={cfgNombre} onChange={(e) => setCfgNombre(e.target.value)} />
              </div>
              <div className="mb-3">
                <MDBInput
                  label="Máximo de jugadores (0 = sin límite)"
                  type="number"
                  min={0}
                  value={cfgMaxJugadores}
                  onChange={(e) => setCfgMaxJugadores(Math.max(0, parseInt(e.target.value) || 0))}
                />
              </div>
              <MDBCheckbox
                label="Vista pública (cualquiera puede ver y unirse desde URL)"
                checked={cfgVistaPublica}
                onChange={(e) => setCfgVistaPublica(e.target.checked)}
              />
              <div className="mb-3 mt-3">
                <label className="form-label fw-semibold" style={{ fontSize: "0.87rem", marginBottom: "0.4rem", display: "block" }}>
                  Modo de evolución
                </label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input className="form-check-input" type="radio" id="evoVis" name="modoEvo"
                      value="VISIBLE" checked={cfgModoEvolucion === "VISIBLE"}
                      onChange={() => setCfgModoEvolucion("VISIBLE")} />
                    <label className="form-check-label" htmlFor="evoVis">
                      <strong>Visible</strong> — se muestra el Pokémon destino y el coste
                    </label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" id="evoOcul" name="modoEvo"
                      value="OCULTA" checked={cfgModoEvolucion === "OCULTA"}
                      onChange={() => setCfgModoEvolucion("OCULTA")} />
                    <label className="form-check-label" htmlFor="evoOcul">
                      <strong>Oculta</strong> — solo se muestra el coste, sin revelar el destino
                    </label>
                  </div>
                </div>
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn size="sm" color="secondary" outline onClick={handleHideConfig}>Cancelar</MDBBtn>
              <MDBBtn size="sm" color="primary" disabled={cfgSaving} onClick={handleSaveConfig}>
                {cfgSaving ? "Guardando…" : "Guardar"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* Join modal */}
      <MDBModal show={showJoinModal} onHide={handleHideJoin} tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Unirse a {game.nombre}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={handleHideJoin} />
            </MDBModalHeader>
            <MDBModalBody>
              <MDBInput
                label="Nombre de tu personaje"
                value={joinNombre}
                onChange={(e) => setJoinNombre(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleJoin(); }}
              />
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn size="sm" color="secondary" outline onClick={handleHideJoin}>Cancelar</MDBBtn>
              <MDBBtn size="sm" color="success" disabled={joinSaving} onClick={handleJoin}>
                {joinSaving ? "Uniéndose…" : "Unirse"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </div>
  );
}
